/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Injectable } from '@angular/core';
import { ApiError } from '@app/shared/models/api/api-error.model';
import { Query } from '@datorama/akita';
import { Observable, of, distinctUntilChanged, map } from 'rxjs';
import { AkitaFeaturedState, TimerComponents } from '../models/featured.state';
import { AkitaFeaturedStore } from './featured.store';
import { Md5 } from 'ts-md5';
import { FeaturedReviewModel } from '@app/shared/models/api/featured-review.model';
import { FeaturedReviewStatsModel } from '@app/shared/models/api/featured-review-stats.model';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';

@Injectable({ providedIn: 'root' })
export class AkitaFeaturedQuery extends Query<AkitaFeaturedState> {
  constructor(
    protected readonly store: AkitaFeaturedStore,
    private readonly akitaRouterQuery: AkitaRouterQuery
  ) {
    super(store);
  }

  public getTimerCountDown(): TimerComponents {
    let dDay = this.getStoredTimerDate().valueOf();

    const milliSecondsInASecond = 1000;
    const hoursInADay = 24;
    const minutesInAnHour = 60;
    const secondsInAMinute = 60;

    const timeDifference = dDay - Date.now();

    if (timeDifference <= 0) {
      this.setStoredTimerDate();
      dDay = this.getStoredTimerDate().valueOf();
    }

    const daysToDday = Math.floor(
      timeDifference /
        (milliSecondsInASecond * minutesInAnHour * secondsInAMinute * hoursInADay)
    );

    const hoursToDday = Math.floor(
      (timeDifference / (milliSecondsInASecond * minutesInAnHour * secondsInAMinute)) %
        hoursInADay
    );

    const minutesToDday = Math.floor(
      (timeDifference / (milliSecondsInASecond * minutesInAnHour)) % secondsInAMinute
    );

    const secondsToDday =
      Math.floor(timeDifference / milliSecondsInASecond) % secondsInAMinute;

    return { secondsToDday, minutesToDday, hoursToDday, daysToDday };
  }

  private getStoredTimerDate(): Date {
    if (this.akitaRouterQuery.isBrowser) {
      try {
        const storedDate = localStorage.getItem('popsy-t-d');
        if (storedDate) {
          return new Date(storedDate);
        } else {
          return this.setStoredTimerDate();
        }
      } catch {
        // ssr
      }
    }

    return new Date();
  }

  private setStoredTimerDate(): Date {
    // set a new date 9 hours ahead from current date
    if (this.akitaRouterQuery.isBrowser) {
      try {
        const newDate = new Date();
        newDate.setHours(newDate.getHours() + 9);
        localStorage.setItem('popsy-t-d', newDate.toISOString());

        return newDate;
      } catch {
        // ssr
      }
    }
    return new Date();
  }

  public getHash(
    sellerId?: string | null,
    model?: string | null,
    brand?: string | null,
    storage?: number | null,
    color?: string | null,
    locale?: string | null,
    currency?: string | null
  ): string {
    return Md5.hashStr(
      `${sellerId || '??'}-${model || '??'}-${brand || '??'}-${storage || ''}-${
        color || ''
      }-${locale || ''}-${currency || ''}`.toUpperCase(),
      false
    ) as string;
  }

  public getFeaturedReviews(
    language?: string | null,
    model?: string | null,
    sortedBy?: string | 'top-ratings'
  ): Array<FeaturedReviewModel> {
    if (language && model) {
      const featuredReviewsForLanguage = this.getValue().featuredReviews[language] || {};
      if (sortedBy === 'bottom-ratings') {
        return FeaturedReviewModel.fromJsonArray(featuredReviewsForLanguage[model]).sort(
          (a, b) => a.rating - b.rating
        );
      }
      return FeaturedReviewModel.fromJsonArray(featuredReviewsForLanguage[model]).sort(
        (a, b) => b.rating - a.rating
      );
    } else {
      return new Array(0);
    }
  }

  public selectFeaturedReviewsError(
    language?: string | null
  ): Observable<ApiError | null> {
    return this.select().pipe(
      map((state: AkitaFeaturedState) => {
        if (language) {
          return state.errorFetchingFeaturedReviews[language] || null;
        } else {
          return null;
        }
      }),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    );
  }

  public selectFetchingFeaturedReviews(language?: string | null): Observable<boolean> {
    return this.select().pipe(
      map((state: AkitaFeaturedState) => {
        if (language) {
          return Boolean(state.fetchingFeaturedReviews[language]);
        } else {
          return false;
        }
      }),
      distinctUntilChanged()
    );
  }

  public selectAllFeaturedReviews(
    language?: string | null
  ): Observable<Array<FeaturedReviewModel>> {
    return this.select().pipe(
      map((state: AkitaFeaturedState) => {
        if (language) {
          const featuredReviewsForLanguage = state.featuredReviews[language] || {};
          const models = Object.keys(featuredReviewsForLanguage);
          let reviews = new Array(0);
          for (const model of models) {
            reviews = FeaturedReviewModel.fromJsonArray([
              ...reviews,
              ...(featuredReviewsForLanguage[model] || []),
            ]);
          }
          return reviews;
        } else {
          return new Array(0);
        }
      }),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    );
  }

  public selectFeaturedReviews(
    language?: string | null,
    model?: string | null,
    sortedBy?: string | 'top-ratings'
  ): Observable<Array<FeaturedReviewModel>> {
    if (model) {
      return this.select().pipe(
        map((state: AkitaFeaturedState) => {
          if (language) {
            const featuredReviewsForLanguage = state.featuredReviews[language] || {};

            if (sortedBy === 'bottom-ratings') {
              return FeaturedReviewModel.fromJsonArray(
                featuredReviewsForLanguage[model]
              ).sort((a, b) => a.rating - b.rating);
            }
            // sortBy = 'top-ratings'
            return FeaturedReviewModel.fromJsonArray(
              featuredReviewsForLanguage[model]
            ).sort((a, b) => b.rating - a.rating);
          } else {
            return new Array(0);
          }
        }),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      );
    } else {
      return of([]);
    }
  }

  public selectFeaturedReviewsStats(
    language?: string | null,
    model?: string | null
  ): Observable<FeaturedReviewStatsModel | null> {
    if (model) {
      return this.select().pipe(
        map((state: AkitaFeaturedState) => {
          if (language) {
            const featuredReviewsStatsForLanguage =
              state.featuredReviewsStats[language] || {};

            return featuredReviewsStatsForLanguage[model];
          } else {
            return null;
          }
        }),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      );
    } else {
      return of(null);
    }
  }

  public getFeaturedReviewsStats(
    language?: string | null,
    model?: string | null
  ): FeaturedReviewStatsModel | null {
    if (model && language) {
      const featuredReviewsStatsForLanguage =
        this.getValue().featuredReviewsStats[language] || {};
      return featuredReviewsStatsForLanguage[model];
    } else {
      return null;
    }
  }
}
